import React, { useState, useEffect, useCallback } from 'react';
import { listPendingVenues, reviewVenue } from '../../services/adminMarketplaceService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../../context/ToastContext';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' }
];

const VenueVerificationQueue = () => {
  const toast = useToast();
  const [venues, setVenues] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const refresh = useCallback(() => {
    listPendingVenues(statusFilter).then(setVenues);
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleApprove = async (id) => {
    await reviewVenue(id, 'approved');
    toast.success('Venue approved');
    refresh();
  };

  const confirmReject = async () => {
    await reviewVenue(rejectTarget, 'rejected', rejectionReason);
    setRejectTarget(null);
    setRejectionReason('');
    toast.success('Venue rejected');
    refresh();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Venue verifications</h2>
      <div className="flex gap-2">
        {STATUS_TABS.map(tab => (
          <button key={tab.id} onClick={() => setStatusFilter(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm ${statusFilter === tab.id ? 'bg-black text-white' : 'bg-white'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      {venues.map(v => (
        <div key={v._id} className="bg-white p-4 rounded-lg shadow">
          <p className="font-semibold">{v.businessName} ({v.businessType})</p>
          <p className="text-sm text-gray-600">{v.userId?.email} — {v.userId?.phone}</p>
          <p className="text-sm">Status: <strong>{v.verification.status}</strong>
            {v.verification.status === 'rejected' && v.verification.rejectionReason && ` — ${v.verification.rejectionReason}`}
          </p>
          <ul className="text-sm mt-2 space-y-1">
            {v.verification.documents.map((doc, i) => (
              <li key={i}>
                <a href={doc.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  {doc.type}
                </a>
              </li>
            ))}
          </ul>
          {v.verification.status === 'pending' && (
            v.verification.documents.length === 0 ? (
              <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-3">
                No verification documents submitted yet — nothing to review.
              </p>
            ) : (
              <div className="flex gap-2 mt-3">
                <button onClick={() => handleApprove(v._id)} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm">
                  Approve
                </button>
                <button onClick={() => setRejectTarget(v._id)} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm">
                  Reject
                </button>
              </div>
            )
          )}
        </div>
      ))}
      {venues.length === 0 && <p className="text-gray-500">No {statusFilter} venues.</p>}

      <ConfirmDialog
        open={!!rejectTarget}
        title="Reject this venue?"
        confirmLabel="Reject"
        danger
        reason={rejectionReason}
        onReasonChange={setRejectionReason}
        reasonLabel="Rejection reason"
        reasonPlaceholder="Why is this venue being rejected?"
        reasonRequired
        onConfirm={confirmReject}
        onCancel={() => { setRejectTarget(null); setRejectionReason(''); }}
      />
    </div>
  );
};

export default VenueVerificationQueue;
